import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { Capacitor } from "@capacitor/core";
import { LocalNotifications } from "@capacitor/local-notifications";
import { TimerNativePlugin } from "./timerPlugin";

// ── Types ──────────────────────────────────────────────────────────────

export type TimerPhase = "running" | "paused";

export type ActiveTimer = {
  id: string;
  totalSeconds: number;
  remaining: number;
  phase: TimerPhase;
  label: string;
  /** Absolute wall-clock ms when a *running* timer hits 0. Undefined when paused. */
  endsAt?: number;
};

type TimerContextValue = {
  timers: Record<string, ActiveTimer>;
  /** Cycles idle→running→paused→idle on each call. */
  tap: (id: string, totalSeconds: number, label: string) => void;
};

const TimerContext = createContext<TimerContextValue | null>(null);

// ── Chime (Web Audio) ──────────────────────────────────────────────────
// In-app sound for when a timer elapses while the app is open. iOS WKWebView
// only lets Web Audio play if the AudioContext was created/resumed inside a
// user gesture, so we unlock it on the start tap (see unlockAudio) and reuse
// that context to sound the chime later without a fresh gesture.

let _audioCtx: AudioContext | null = null;

function getAudioCtx(): AudioContext | null {
  try {
    if (!_audioCtx) {
      const Ctor =
        window.AudioContext ??
        (window as unknown as { webkitAudioContext?: typeof AudioContext })
          .webkitAudioContext;
      if (!Ctor) return null;
      _audioCtx = new Ctor();
    }
    return _audioCtx;
  } catch {
    return null;
  }
}

/** Resume the audio context from a user gesture so the chime can play later. */
function unlockAudio() {
  const ctx = getAudioCtx();
  if (ctx && ctx.state === "suspended") ctx.resume().catch(() => {});
}

/** Two short rising sine tones — a pleasant "ding-ding" with no audio asset. */
function playChime() {
  const ctx = getAudioCtx();
  if (!ctx) return;
  const resume = ctx.state === "suspended" ? ctx.resume() : Promise.resolve();
  resume
    .then(() => {
      const now = ctx.currentTime;
      for (const [freq, at] of [
        [880, 0],
        [1320, 0.18],
      ] as const) {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "sine";
        osc.frequency.value = freq;
        const start = now + at;
        // Quick attack, gentle exponential decay — avoids clicks.
        gain.gain.setValueAtTime(0.0001, start);
        gain.gain.exponentialRampToValueAtTime(0.3, start + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.4);
        osc.connect(gain).connect(ctx.destination);
        osc.start(start);
        osc.stop(start + 0.45);
      }
    })
    .catch(() => {});
}

// ── Notification helpers ───────────────────────────────────────────────

let _notifSeq = 3000;

async function requestPermission() {
  if (Capacitor.isNativePlatform()) {
    try {
      const { display } = await LocalNotifications.requestPermissions();
      return display === "granted";
    } catch {
      return false;
    }
  }
  if ("Notification" in window && Notification.permission === "default") {
    return (await Notification.requestPermission()) === "granted";
  }
  return "Notification" in window && Notification.permission === "granted";
}

async function scheduleNativeNotif(notifId: number, label: string, remainingSeconds: number) {
  try {
    await LocalNotifications.schedule({
      notifications: [
        {
          id: notifId,
          title: "Timer done! ⏰",
          body: `${label} is ready.`,
          schedule: { at: new Date(Date.now() + remainingSeconds * 1000) },
          // Non-matching name → iOS falls back to the default notification
          // sound, so the lock screen / backgrounded app chimes.
          sound: "default",
          // Suppress the foreground banner+sound; playChime() handles the
          // app-open case, so this avoids a double chime.
          silent: true,
          extra: {},
        },
      ],
    });
  } catch {
    // Plugin not in native binary yet — graceful no-op
  }
}

async function cancelNativeNotif(notifId: number) {
  try {
    await LocalNotifications.cancel({ notifications: [{ id: notifId }] });
  } catch {
    // ignore
  }
}

// ── Provider ──────────────────────────────────────────────────────────

export function TimerProvider({ children }: { children: ReactNode }) {
  const [timers, setTimers] = useState<Record<string, ActiveTimer>>({});
  const ref = useRef<Record<string, ActiveTimer>>({});
  const notifIds = useRef<Record<string, number>>({});
  const permRequested = useRef(false);

  useEffect(() => {
    ref.current = timers;
  }, [timers]);

  // Recomputes every running timer's remaining from wall-clock time, fires
  // completion for any that have elapsed, and returns the next state map.
  // Wall-clock based so it self-corrects after the WKWebView is suspended in
  // the background (JS timers freeze there; this catches up on the next tick).
  const recompute = useCallback(() => {
    const prev = ref.current;
    const next: Record<string, ActiveTimer> = {};
    let changed = false;

    for (const id of Object.keys(prev)) {
      const t = prev[id];
      if (t.phase !== "running" || t.endsAt == null) {
        next[id] = t;
        continue;
      }
      const rem = Math.max(0, Math.ceil((t.endsAt - Date.now()) / 1000));
      if (rem !== t.remaining) changed = true;
      if (rem <= 0) {
        // Timer done — entry removed. Native notif already fired at endsAt.
        // In-app chime: reliable while the app is open (native + web). The
        // native notif is scheduled silent in the foreground, so no double.
        playChime();
        // Web fallback: fire inline since WKWebView can't background notify.
        if (
          !Capacitor.isNativePlatform() &&
          "Notification" in window &&
          Notification.permission === "granted"
        ) {
          new Notification("Timer done! ⏰", { body: `${t.label} is ready.` });
        }
        delete notifIds.current[id];
        TimerNativePlugin.endActivity({ id }).catch(() => {});
        changed = true;
      } else {
        next[id] = { ...t, remaining: rem };
      }
    }

    if (changed) {
      ref.current = next;
      setTimers(next);
    }
  }, []);

  // Single interval ticks all running timers.
  useEffect(() => {
    const tick = setInterval(recompute, 1000);
    return () => clearInterval(tick);
  }, [recompute]);

  // When the app/tab returns to the foreground, recompute immediately so a
  // timer that elapsed (or advanced) while backgrounded snaps to the truth.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") recompute();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", recompute);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", recompute);
    };
  }, [recompute]);

  const tap = useCallback(async (id: string, totalSeconds: number, label: string) => {
    const t = ref.current[id];

    // Any tap is a user gesture — unlock audio so the chime can sound on iOS
    // when the timer later elapses without a fresh gesture.
    unlockAudio();

    if (!t) {
      // idle → running (fresh start)
      if (!permRequested.current) {
        permRequested.current = true;
        await requestPermission();
      }
      const notifId = _notifSeq++;
      notifIds.current[id] = notifId;
      if (Capacitor.isNativePlatform()) {
        await scheduleNativeNotif(notifId, label, totalSeconds);
        TimerNativePlugin.startActivity({ id, label, remaining: totalSeconds })
          .then((r) => {
            if (!r.started)
              window.alert(`Live Activity failed: ${r.reason ?? "unknown"}`);
          })
          .catch((e) => window.alert(`Live Activity error: ${String(e)}`));
      }
      const timer: ActiveTimer = {
        id,
        totalSeconds,
        remaining: totalSeconds,
        phase: "running",
        label,
        endsAt: Date.now() + totalSeconds * 1000,
      };
      ref.current = { ...ref.current, [id]: timer };
      setTimers({ ...ref.current });
      return;
    }

    if (t.phase === "running") {
      // running → paused
      if (notifIds.current[id] != null) {
        cancelNativeNotif(notifIds.current[id]);
        delete notifIds.current[id];
      }
      // Freeze remaining at the current wall-clock value, drop endsAt.
      const frozen =
        t.endsAt != null
          ? Math.max(0, Math.ceil((t.endsAt - Date.now()) / 1000))
          : t.remaining;
      if (Capacitor.isNativePlatform()) {
        TimerNativePlugin.pauseActivity({ id, remaining: frozen }).catch(() => {});
      }
      ref.current = {
        ...ref.current,
        [id]: { ...t, phase: "paused", remaining: frozen, endsAt: undefined },
      };
      setTimers({ ...ref.current });
      return;
    }

    // paused → idle (reset)
    if (Capacitor.isNativePlatform()) {
      TimerNativePlugin.endActivity({ id }).catch(() => {});
    }
    const updated = { ...ref.current };
    delete updated[id];
    ref.current = updated;
    setTimers(updated);
  }, []);

  const value = useMemo<TimerContextValue>(() => ({ timers, tap }), [timers, tap]);

  return <TimerContext.Provider value={value}>{children}</TimerContext.Provider>;
}

export function useTimers(): TimerContextValue {
  const ctx = useContext(TimerContext);
  if (!ctx) throw new Error("useTimers must be used within TimerProvider");
  return ctx;
}

// ── Helpers ────────────────────────────────────────────────────────────

export function fmtTimer(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}
